import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight, MapPin, Users, MessageCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { fetchProducts } from '../data/products';
import { trackPageView } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';
import type { Product } from '../types';
import { SITE_NAME, SITE_TAGLINE, WHATSAPP_NUMBER } from '../lib/brand';

function shuffleProducts<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Home() {
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroError, setHeroError] = useState(false);
  const { getValue, getHtml, loading: cmsLoading } = useCMS();
  const heroImage = getValue('home.hero.image', '/logo.png');

  useEffect(() => {
    trackPageView('/');
    fetchProducts().then((data) => {
      const shuffled = shuffleProducts(data);
      setProducts(shuffled.slice(0, 3));
      setAllProducts(shuffled);
      setLoading(false);
    });
  }, []);

  // 市集輪播：每 4 秒隨機換一張卡，展示商品多樣性（分頁隱藏時暫停）
  useEffect(() => {
    if (allProducts.length <= 3) return undefined;
    let slot = 0;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      setProducts((current) => {
        const visibleIds = new Set(current.map((p) => p.id));
        const pool = allProducts.filter((p) => !visibleIds.has(p.id));
        if (pool.length === 0) return current;
        const next = pool[Math.floor(Math.random() * pool.length)];
        const copy = [...current];
        copy[slot % copy.length] = next;
        slot += 1;
        return copy;
      });
    }, 4000);
    return () => window.clearInterval(timer);
  }, [allProducts]);

  const whatsappNumber = getValue('site.whatsapp.number', WHATSAPP_NUMBER);
  const whatsappFriendLink = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent('你好，我想查詢')}`;
  const whatsappGiftLink = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent('你好，我想了解優惠')}`;

  const trustItems = [
    { icon: MapPin, titleKey: 'home.trust.1.title', descKey: 'home.trust.1.description' },
    { icon: Users, titleKey: 'home.trust.2.title', descKey: 'home.trust.2.description' },
    { icon: MessageCircle, titleKey: 'home.trust.3.title', descKey: 'home.trust.3.description' },
  ];

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">載入中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-x1493-dark text-white overflow-hidden min-h-[600px] md:min-h-[700px] flex items-center">
        {!heroError && (
          <img
            src={heroImage}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            onError={() => setHeroError(true)}
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{ transform: 'translateZ(0)' }}
          />
        )}
        <div className="absolute inset-0 bg-transparent" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-x1493-primary rounded-full blur-[120px] transform translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-x1493-secondary rounded-full blur-[100px] transform -translate-x-1/3 translate-y-1/3" />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-20 md:py-32 relative z-10 w-full h-full">
          <div className="max-w-2xl h-full flex flex-col md:justify-center">
            <div className="flex-1 md:flex-none flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm mb-6 w-fit">
                <Flame size={16} className="text-x1493-secondary" />
                <span dangerouslySetInnerHTML={getHtml('home.hero.badge', SITE_TAGLINE)} />
              </div>
              <h1
                className="text-4xl md:text-6xl font-bold leading-tight mb-6"
                dangerouslySetInnerHTML={getHtml('home.hero.title', '精選好物<br />等你發掘')}
              />
              <p
                className="text-xl text-white font-bold mb-8 leading-relaxed bg-black/50 backdrop-blur-sm px-4 py-3 rounded-xl md:bg-transparent md:backdrop-blur-none md:px-0 md:py-0 md:rounded-none"
                dangerouslySetInnerHTML={getHtml('home.hero.subtitle', '一站式網上選購，方便快捷。<br class="hidden md:block" />WhatsApp 落單，專人跟進。')}
              />
              </div>
            <div className="flex flex-col sm:flex-row gap-4 mt-auto md:mt-0">
              <Link to="/store" className="btn-primary text-center flex items-center justify-center gap-2">
                {getValue('home.hero.primaryButtonText', '逛商店')}
                <ArrowRight size={20} />
              </Link>
              <a
                href={whatsappFriendLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline text-center flex items-center justify-center gap-2"
              >
                <MessageCircle size={20} />
                {getValue('home.hero.secondaryButtonText', 'WhatsApp 查詢')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trustItems.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <div className="w-14 h-14 bg-x1493-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-x1493-primary" size={28} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{getValue(item.titleKey)}</h3>
                  <p className="text-stone-600 text-sm">{getValue(item.descKey)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Highlights */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">人氣辣物</h2>
            <p className="text-stone-600">辣粉、醬料、零食到各地名產，由微辣到地獄辣，總有一款啱你</p>
          </div>

          {loading ? (
            <div className="text-center py-12">載入中...</div>
          ) : (
            <>
              <style>{`
                @keyframes home-product-in { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: none; } }
                .home-product-swap { animation: home-product-in 0.55s ease-out both; }
                @media (prefers-reduced-motion: reduce) { .home-product-swap { animation: none; } }
              `}</style>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {products.map((product) => (
                  <div key={product.id} className="home-product-swap">
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="text-center mt-10">
            <Link to="/products" className="btn-outline inline-flex items-center gap-2">
              查看全部產品
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Story Teaser */}
      <section className="py-16 bg-x1493-dark text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">{getValue('home.story.title', '我們的故事')}</h2>
              <p
                className="text-white/80 mb-6 leading-relaxed"
                dangerouslySetInnerHTML={getHtml('home.story.content', '在這裡寫下品牌的起點故事：為何而做、為誰而做、堅持什麼。真實的故事最動人。（後台 CMS 可編輯）')}
              />
              <Link to="/story" className="btn-secondary inline-flex items-center gap-2">
                {getValue('home.story.buttonText', '閱讀完整故事')}
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="bg-white/5 rounded-3xl p-8 border border-white/10 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div className="space-y-6">
                {/* Sponsor Badge above first item */}
                <div className="inline-flex items-center gap-1.5 bg-x1493-primary/20 border border-x1493-primary/40 rounded-full px-3 py-1">
                  <span className="w-2 h-2 bg-x1493-primary rounded-full animate-pulse" />
                  <span className="text-x1493-primary text-xs font-bold tracking-wide">Sponsor 贊助</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-x1493-primary flex items-center justify-center font-bold shrink-0">1493</div>
                  <div>
                    <p className="font-bold">我們的起點</p>
                    <p className="text-sm text-white/60">一場美麗的誤會</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-x1493-secondary flex items-center justify-center font-bold shrink-0">香港</div>
                  <div>
                    <p className="font-bold">港式辣文化</p>
                    <p className="text-sm text-white/60">街頭與家的味道</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-x1493-accent flex items-center justify-center shrink-0">
                    <Flame className="text-x1493-dark" size={24} />
                  </div>
                  <div>
                    <p className="font-bold">傳承火熱</p>
                    <p className="text-sm text-white/60">將正宗帶給你</p>
                  </div>
                </div>
              </div>
              <div className="relative group flex items-center justify-center">
                {/* Pulse ring layers */}
                <div className="absolute w-48 h-48 md:w-56 md:h-56 rounded-full bg-x1493-primary/30 animate-pulse-ring" />
                <div className="absolute w-48 h-48 md:w-56 md:h-56 rounded-full bg-x1493-secondary/20 animate-pulse-ring-delay" />
                <img
                  src="/media/media/1786062241_x1493.webp"
                  alt="贊助商廣告"
                  className="relative w-48 h-48 md:w-56 md:h-56 object-cover rounded-full border-4 border-x1493-primary/40 shadow-lg group-hover:scale-[1.03] transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gift Code CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-r from-x1493-primary to-x1493-secondary rounded-3xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">{getValue('home.giftCta.title', '想獲得專屬禮品碼？')}</h2>
            <p
              className="text-white/90 mb-8 max-w-xl mx-auto"
              dangerouslySetInnerHTML={getHtml('home.giftCta.description', '不定期有專屬優惠。聯繫我們，成為會員一族。')}
            />
            <a
              href={whatsappGiftLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-x1493-primary font-bold px-8 py-4 rounded-2xl hover:bg-stone-100 transition"
            >
              <MessageCircle size={22} />
              {getValue('home.giftCta.buttonText', '聯繫我們領取優惠')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
